import { useState } from 'react'

import { Badge, Card, Loaded, Modal, Notice, Page, Table } from '../components/ui'
import { get } from '../lib/api'
import { count, dateTime } from '../lib/format'
import { useResource } from '../lib/useResource'

type Entry = {
  id: string
  at: string
  actor_id: string | null
  action: string
  target_type: string
  target_id: string | null
  reason: string
  ip: string
  user_agent?: string
  request_id: string
  before?: Record<string, unknown> | null
  after?: Record<string, unknown> | null
}

/** Actions that describe someone reaching PHI. Highlighted because these are the
 *  rows an auditor is actually looking for. */
const SENSITIVE = new Set(['admin.phi_access', 'admin.ai_prompt_read', 'auth.refresh_reuse_detected'])

function toCsv(rows: Entry[]): string {
  const headers = ['at', 'actor_id', 'action', 'target_type', 'target_id', 'reason', 'ip', 'request_id']
  const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`
  return [
    headers.join(','),
    ...rows.map((row) =>
      headers.map((key) => escape((row as unknown as Record<string, unknown>)[key])).join(','),
    ),
  ].join('\n')
}

export function Audit() {
  const [action, setAction] = useState('')
  const [targetId, setTargetId] = useState('')
  const [applied, setApplied] = useState({ action: '', target_id: '' })
  const [inspecting, setInspecting] = useState<Entry | null>(null)

  const entries = useResource(
    (signal) =>
      get<Entry[]>(
        '/v1/admin/audit',
        { action: applied.action, target_id: applied.target_id, limit: 500 },
        signal,
      ),
    [applied.action, applied.target_id],
  )

  const download = () => {
    const rows = entries.data ?? []
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `seyon-audit-${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Page
      title="Audit log"
      subtitle="Append-only. A database trigger rejects UPDATE and DELETE on this table, so no surface in this product — including this console — can alter a row."
      actions={
        <>
          <button className="btn btn--sm" onClick={download} disabled={!entries.data?.length}>
            Export CSV
          </button>
        </>
      }
    >
      <Notice>
        The exported CSV contains actor identifiers, IP addresses and free-text reasons. It is a
        record of who looked at whose data — treat the file the way you would treat the records it
        describes.
      </Notice>

      <Card
        title="Filter"
        action={
          <div className="row">
            <button
              className="btn btn--sm"
              onClick={() => setApplied({ action: action.trim(), target_id: targetId.trim() })}
            >
              Apply
            </button>
            {(applied.action || applied.target_id) && (
              <button
                className="btn btn--sm btn--ghost"
                onClick={() => {
                  setAction('')
                  setTargetId('')
                  setApplied({ action: '', target_id: '' })
                }}
              >
                Clear
              </button>
            )}
          </div>
        }
      >
        <div className="row row--wrap">
          <div className="field" style={{ width: 260 }}>
            <label className="field__label" htmlFor="action">
              Action (exact match)
            </label>
            <input
              id="action"
              className="input"
              placeholder="admin.phi_access"
              value={action}
              onChange={(event) => setAction(event.target.value)}
            />
          </div>
          <div className="field" style={{ width: 300 }}>
            <label className="field__label" htmlFor="target">
              Target id
            </label>
            <input
              id="target"
              className="input"
              placeholder="user id, model id, event id…"
              value={targetId}
              onChange={(event) => setTargetId(event.target.value)}
            />
          </div>
          <div className="row row--wrap" style={{ alignSelf: 'flex-end', paddingBottom: 6 }}>
            {['admin.phi_access', 'admin.role_grant', 'admin.user_suspend', 'auth.refresh_reuse_detected'].map(
              (preset) => (
                <button
                  key={preset}
                  className="btn btn--sm btn--ghost"
                  onClick={() => {
                    setAction(preset)
                    setApplied({ action: preset, target_id: targetId.trim() })
                  }}
                >
                  {preset}
                </button>
              ),
            )}
          </div>
        </div>
      </Card>

      <Card flush>
        <Loaded resource={entries}>
          {(rows) => (
            <>
              <Table
                rows={rows}
                rowKey={(row) => row.id}
                onRowClick={(row) => setInspecting(row)}
                empty="No entries match this filter."
                columns={[
                  { key: 'at', header: 'When', render: (row) => <span className="dim">{dateTime(row.at)}</span> },
                  {
                    key: 'action',
                    header: 'Action',
                    strong: true,
                    render: (row) =>
                      SENSITIVE.has(row.action) ? (
                        <Badge tone="alert">{row.action}</Badge>
                      ) : (
                        <span className="mono">{row.action}</span>
                      ),
                  },
                  { key: 'actor', header: 'Actor', render: (row) => <span className="mono">{row.actor_id ?? 'system'}</span> },
                  {
                    key: 'target',
                    header: 'Target',
                    render: (row) => (
                      <span>
                        <span className="dim" style={{ fontSize: 10.5, display: 'block' }}>
                          {row.target_type || '—'}
                        </span>
                        <span className="mono">{row.target_id ?? '—'}</span>
                      </span>
                    ),
                  },
                  {
                    key: 'reason',
                    header: 'Reason',
                    render: (row) => (
                      <span style={{ maxWidth: 340, display: 'block' }}>{row.reason || <span className="dim">—</span>}</span>
                    ),
                  },
                  { key: 'ip', header: 'IP', render: (row) => <span className="mono dim">{row.ip || '—'}</span> },
                ]}
              />
              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--divider)' }}>
                <span className="dim" style={{ fontSize: 11.5 }}>
                  Showing {count(rows.length)} entries, newest first. The API caps a single read at
                  500 — narrow the filter rather than assuming this is everything.
                </span>
              </div>
            </>
          )}
        </Loaded>
      </Card>

      {inspecting && (
        <Modal title={inspecting.action} onClose={() => setInspecting(null)}>
          <div className="stack">
            <div className="row row--wrap">
              <Badge>{dateTime(inspecting.at)}</Badge>
              <Badge>{inspecting.target_type || 'no target'}</Badge>
              {SENSITIVE.has(inspecting.action) && <Badge tone="alert">Sensitive</Badge>}
            </div>
            <dl className="stack" style={{ margin: 0, gap: 8, fontSize: 12.5 }}>
              <Field label="Actor" value={inspecting.actor_id ?? 'system'} />
              <Field label="Target" value={inspecting.target_id ?? '—'} />
              <Field label="Request id" value={inspecting.request_id || '—'} />
              <Field label="IP" value={inspecting.ip || '—'} />
              <Field label="Reason" value={inspecting.reason || '—'} />
            </dl>
            {(inspecting.before || inspecting.after) && (
              <div className="grid grid--2" style={{ gap: 12 }}>
                <Diff title="Before" value={inspecting.before} />
                <Diff title="After" value={inspecting.after} />
              </div>
            )}
          </div>
        </Modal>
      )}
    </Page>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ alignItems: 'baseline' }}>
      <dt className="dim" style={{ width: 100, flex: 'none', fontSize: 11.5 }}>
        {label}
      </dt>
      <dd className="mono" style={{ margin: 0, wordBreak: 'break-all' }}>
        {value}
      </dd>
    </div>
  )
}

function Diff({ title, value }: { title: string; value: Record<string, unknown> | null | undefined }) {
  return (
    <div className="field">
      <span className="field__label">{title}</span>
      <pre
        className="mono"
        style={{
          margin: 0,
          padding: 12,
          background: 'var(--surface-inset)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          fontSize: 11,
          overflowX: 'auto',
        }}
      >
        {value ? JSON.stringify(value, null, 2) : '—'}
      </pre>
    </div>
  )
}
